import { useCallback, useEffect, useRef, useState } from 'react'
import { Card, Collapse, Flex, Grid, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { CreditCardOutlined, UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { STICKY_TOP } from '@/app/layout/layoutMetrics'
import { CashierBalanceBar } from '@/features/cashdesk/CashierBalanceBar'
import { PreselectNotice } from '@/features/cashdesk/PreselectNotice'
import { SubscriberCardBody } from '@/features/cashdesk/SubscriberCardBody'
import { SubscriberCardDrawer } from '@/features/cashdesk/SubscriberCardDrawer'
import { SubscriberSearch } from '@/features/cashdesk/SubscriberSearch'
import { VoucherActivateForm } from '@/features/voucher/VoucherActivateForm'
import { SubscriberHistoryDrawer, type HistorySubscriber } from '@/features/history/SubscriberHistoryDrawer'
import { PERM } from '@/shared/auth/partnerPermissions'
import { useAuth } from '@/shared/auth/useAuth'
import { useCashPlans } from '@/features/cashdesk/cashdeskPlans'
import { useCashier } from '@/features/cashdesk/useCashier'
import { usePreselectedSubscriber } from '@/features/cashdesk/usePreselectedSubscriber'
import type { DirectoryRow } from '@/features/cashdesk/cashdeskApi'
import { fetchDeniedPlans } from '@/features/subscriber/subscriberApi'
import { subscriberKeys } from '@/features/subscriber/subscriberKeys'
import {
  PRESELECT_ACTION_PARAM,
  PRESELECT_LOGIN_PARAM,
  readPreselectAction,
} from '@/shared/lib/cashdeskLink'
import { EmptyState } from '@/shared/ui/EmptyState'

/**
 * BIR USTUNLI tartibning eng katta kengligi.
 *
 * ⚠️ Bu son endi faqat `lg` DAN TOR oynada ishlaydi: telefon, planshet
 * va tor kompyuter oynasi. Keng ekranda tartib ikki ustunli
 * ({@link SPLIT_LEFT}) va bu chegara umuman qo'llanmaydi.
 */
const MAX_WIDTH = 560

/**
 * Ikki ustunli tartibda CHAP ustunning kengligi.
 *
 * ⚠️ Qidiruv natijasi kartochka bo'lib qoladi (`SubscriberSearch`
 * javadoc'i: 390px da beshta ustun bir-biriga yopishadi), ya'ni chap
 * ustun kenglikdan foyda ko'rmaydi — kenglik O'NG ustunga, ish
 * bajariladigan joyga beriladi.
 */
const SPLIT_LEFT = 420

/**
 * «Kassa» — kassirning yagona ish ekrani.
 *
 * <h2>Nima uchun bu sahifa admin panelidagilarga o'xshamaydi</h2>
 * Admin sahifalari jadval + filtr + modal naqshi bo'yicha qurilgan va
 * ular kompyuter uchun (ADR-030 §4). Kassir esa ko'chada, do'konda,
 * telefon bilan va abonent qarshisida turib ishlaydi. Shuning uchun
 * tor ekranda:
 *
 *   · bitta ustun, bosh barmoq yetadigan nishonlar (52–76px)
 *   · qidiruv eng tepada va fokus avtomatik — ish shundan boshlanadi
 *   · depozit yopishqoq: u tugasa ish to'xtaydi
 *   · jadval yo'q, sahifalash yo'q, ustun sozlamalari yo'q
 *   · amallar pastdan chiqadigan panelda — telefonni qo'lda surmasdan
 *
 * <h2>⚠️ KENG EKRANDA IKKI USTUN — `docs/11 §4.1`</h2>
 * Egasi talabi (2026-08-20): «agentlar ko'pincha kompyuterda o'tiradi».
 * O'lchangan holat: 1920px li monitorda 560px lik tasma, ya'ni 71%
 * bo'sh, ustiga kartochka o'ng chekkadagi 460px lik `Drawer` da va
 * qolgan ekran niqob ostida. Ya'ni qidiruv natijasi bilan kartochkani
 * BIR VAQTDA ishlatib bo'lmasdi — kassirning eng ko'p takrorlanadigan
 * ishi aynan shu.
 *
 * Endi `lg` dan keng oynada: chapda qidiruv va depozit, o'ngda
 * kartochka. `Drawer` u yerda umuman ochilmaydi.
 *
 * ⚠️ TOR EKRAN TEGILMAGAN: `lg` dan tor oynada bugungi kodning aynan
 * o'zi ishlaydi — bitta ustun va pastdan chiqadigan `Drawer`.
 * Kassirning asosiy qurilmasi telefon va bu tartib ADR-030 §4 bilan
 * qotirilgan.
 *
 * ⚠️ QIDIRUV NATIJASI JADVALGA AYLANMADI. Kenglik paydo bo'lgani uni
 * `DataTable` qilish sababi emas — `SubscriberSearch` javadoc'idagi
 * qaror kuchda qoladi va o'ng ustun uni baribir tor qoldiradi.
 *
 * <h2>⚠️ ABONENT URL'DAN HAM KELISHI MUMKIN (egasi talabi 2026-08-21)</h2>
 * «Abonentlar» ekranidagi «Balansni to'ldirish» va «Paket berish»
 * amallari shu ekranga `?login=…&action=…` bilan keladi — ya'ni pul va
 * paket BITTA joydan boshqariladi. Shartnoma `shared/lib/cashdeskLink`
 * da va u ikkala tomonda AYNI fayldan o'qiladi.
 *
 * ⚠️ Topilmagan yoki ochilmagan abonent BO'SH KARTOCHKA bermaydi —
 * sababi yoziladi (`PreselectNotice`).
 */
export function CashdeskPage() {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const { can } = useAuth()
  const [agentId, setAgentId] = useState<string | null>(null)
  const [selected, setSelected] = useState<DirectoryRow | null>(null)
  const [historyOf, setHistoryOf] = useState<HistorySubscriber | null>(null)

  const cashier = useCashier(agentId)
  const plans = useCashPlans()
  const denied = useQuery({
    queryKey: subscriberKeys.deniedPlans(selected?.branchNodeId ?? null),
    queryFn: () => fetchDeniedPlans(selected?.branchNodeId ?? null),
    staleTime: 60_000,
    enabled: selected !== null,
  })
  const sellablePlans = plans.sellable.filter((plan) => !denied.data?.has(plan.id))

  const [params, setParams] = useSearchParams()
  const wantedLogin = params.get(PRESELECT_LOGIN_PARAM)
  const wantedAction = readPreselectAction(params.get(PRESELECT_ACTION_PARAM))
  const preselect = usePreselectedSubscriber(wantedLogin)

  /**
   * URL'dagi tanlov niyatini o'chiradi.
   *
   * ⚠️ Kassir BOSHQA abonentni ochganda ham chaqiriladi: aks holda URL
   * ekranda turgandan boshqa odamni ko'rsatib turardi va sahifa qayta
   * yuklanganda ish YARIM yo'ldan eski abonentga qaytardi.
   */
  const clearPreselect = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(PRESELECT_LOGIN_PARAM)
        next.delete(PRESELECT_ACTION_PARAM)
        return next
      },
      { replace: true },
    )
  }, [setParams])

  const refresh = useCallback((row: DirectoryRow) => {
    setSelected((prev) => (prev !== null && prev.id === row.id ? row : prev))
  }, [])

  /*
    ⚠️ URL'dagi login BIR MARTA ochadi, keyin faqat YANGILAYDI.

    Birinchi yurishda kartochka ochiladi. Keyingi yurishlar (pul amalidan
    keyin kesh bekor qilinganda) esa faqat ayni abonentning YANGI qatorini
    qo'yadi — `SubscriberSearch.onRefresh` bilan bir xil sabab: ekrandagi
    balans eskirib qolmasin. Qayta OCHISH bo'lmasligi ham shart, aks holda
    kassir yopgan kartochka o'zidan qaytib chiqardi.
  */
  const applied = useRef<string | null>(null)
  useEffect(() => {
    if (preselect.kind !== 'found') return
    const row = preselect.row
    if (applied.current !== preselect.login) {
      applied.current = preselect.login
      setSelected(row)
      return
    }
    setSelected((prev) => (prev !== null && prev.id === row.id ? row : prev))
  }, [preselect])

  const openFromSearch = useCallback(
    (row: DirectoryRow) => {
      setSelected(row)
      clearPreselect()
    },
    [clearPreselect],
  )

  const closeCard = useCallback(() => {
    setSelected(null)
    clearPreselect()
  }, [clearPreselect])

  const isMobile = screens.md === false
  const wide = screens.lg === true

  /*
    ⚠️ «Karta faollashtirish» KASSA ICHIDA — egasi talabi 2026-08-25:
    «karta faollashtirish menyu kerakmas, uni kassa menyuga olib
    o'tsak bo'ladi, chunki uni vazifasi ham shunaqa».

    ⚠️ YIG'ILGAN HOLDA ochiladi va bu o'lchangan qaror, bezak emas:
    kassirning ishi qidiruvdan boshlanadi, ochiq forma esa qidiruv
    natijasini telefon ekranida pastga surib yuborardi.

    ⛔ ALOHIDA SAHIFA (`/voucher`) O'CHIRILMADI: `voucher.activate`
    kassa bayrog'idan mustaqil va kassasiz reseller Kassa ekranini
    umuman ko'rmaydi (`useVisibleNav` dagi to'liq sabab). Ular uchun
    menyu bandi ham, sahifa ham joyida qoladi.
  */
  const voucherSection = can(PERM.voucherActivate) ? (
    <Collapse
      size="small"
      style={{ marginTop: 16 }}
      items={[
        {
          key: 'voucher',
          label: t('vouchers.title'),
          extra: <CreditCardOutlined />,
          children: <VoucherActivateForm />,
        },
      ]}
    />
  ) : null

  const workspace = (
    <>
      <CashierBalanceBar cashier={cashier} onSelectAgent={setAgentId} />
      <SubscriberSearch
        onOpen={openFromSearch}
        selectedId={selected?.id ?? null}
        onRefresh={refresh}
      />
      {voucherSection}
    </>
  )

  /** ⚠️ Panel FAQAT URL bilan kelgan abonentda ochiladi — qidiruvdan emas */
  const initialMode =
    preselect.kind === 'found' && selected?.id === preselect.row.id ? wantedAction : null

  const showHistory =
    selected !== null && can(PERM.subscriberHistoryView)
      ? () => setHistoryOf({ id: selected.id, login: selected.login })
      : null

  return (
    <div style={{ maxWidth: wide ? undefined : MAX_WIDTH, margin: '0 auto' }}>
      {/*
        Sarlavha faqat kompyuterda. Telefonda uni kabinetning yuqori
        qatori ko'rsatadi, takrori esa 844px balandlikning ~60px ini yeb
        qo'yardi — o'sha joyga bitta qidiruv natijasi sig'adi.
      */}
      {isMobile ? null : (
        <div style={{ marginBottom: 16 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('cashdesk.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('cashdesk.subtitle')}</Typography.Text>
        </div>
      )}

      <PreselectNotice state={preselect} onDismiss={clearPreselect} />

      {wide ? (
        <Flex gap={20} align="flex-start">
          <div style={{ flex: `0 0 ${SPLIT_LEFT}px`, minWidth: 0 }}>{workspace}</div>

          {/* ⚠️ O'ng ustun YOPISHQOQ va u sarlavha qatori OSTIDAN
              boshlanadi (`STICKY_TOP`): `top: 0` bilan u
              `.xsib-header` ning ortiga kirib ketardi. Uzun qidiruv
              natijasini aylantirgan kassir uchun kartochka va amal
              tugmalari ekranda qoladi. */}
          <div style={{ flex: '1 1 0', minWidth: 0, position: 'sticky', top: STICKY_TOP }}>
            <Card size="small" title={selected?.login ?? t('cashdesk.pickTitle')}>
              {selected === null ? (
                <EmptyState
                  icon={<UserOutlined />}
                  title={t('cashdesk.pickTitle')}
                  description={t('cashdesk.pickText')}
                />
              ) : (
                <SubscriberCardBody
                  // ⚠️ `key` MAJBURIY: usiz boshqa abonent tanlanganda
                  // ich QAYTA MOUNT BO'LMASDI va ochiq turgan pul
                  // paneli yangi abonentga tegishli bo'lib ko'rinardi.
                  // `Drawer` da buni `destroyOnHidden` qilardi.
                  key={selected.id}
                  row={selected}
                  cashier={cashier}
                  plans={sellablePlans}
                  priceByName={plans.priceByName}
                  initialMode={initialMode}
                  onShowHistory={showHistory}
                />
              )}
            </Card>
          </div>
        </Flex>
      ) : (
        <>
          {workspace}
          <SubscriberCardDrawer
            row={selected}
            cashier={cashier}
            plans={sellablePlans}
            priceByName={plans.priceByName}
            initialMode={initialMode}
            onShowHistory={showHistory}
            onClose={closeCard}
          />
        </>
      )}

      <SubscriberHistoryDrawer subscriber={historyOf} onClose={() => setHistoryOf(null)} />
    </div>
  )
}
